import { useEffect, useState } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, MoveUp, MoveDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import * as lucideIcons from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const languageFlags = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ar: "🇸🇦"
};

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState("tr");
  const { toast } = useToast();

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line
  }, []);

  async function fetchServices() {
    setLoading(true);
    const { data, error } = await supabase
      .from("home_services")
      .select("id, title, description, icon, order_num")
      .order("order_num", { ascending: true });
    if (error) {
      toast({ title: "Hata", description: "Hizmetler yüklenemedi.", variant: "destructive" });
    } else {
      // JSON verilerini parse et
      const parsedServices = (data || []).map(service => ({
        ...service,
        title: typeof service.title === 'string' ? JSON.parse(service.title) : service.title,
        description: typeof service.description === 'string' ? JSON.parse(service.description) : service.description
      }));
      setServices(parsedServices);
    }
    setLoading(false);
  }

  const moveService = async (id, direction) => {
    const index = services.findIndex((service) => service.id === id);
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === services.length - 1)
    ) {
      return;
    }
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    const updatedServices = [...services];
    [updatedServices[index], updatedServices[swapIndex]] = [updatedServices[swapIndex], updatedServices[index]];
    // Sıralama numaralarını güncelle
    for (let i = 0; i < updatedServices.length; i++) {
      updatedServices[i].order_num = i + 1;
      await supabase.from("home_services").update({ order_num: i + 1 }).eq("id", updatedServices[i].id);
    }
    setServices([...updatedServices]);
  };

  const deleteService = async (id) => {
    const { error } = await supabase.from("home_services").delete().eq("id", id);
    if (error) {
      toast({ title: "Hata", description: "Hizmet silinemedi.", variant: "destructive" });
    } else {
      setServices(services.filter((service) => service.id !== id));
      toast({ title: "Başarılı", description: "Hizmet silindi." });
    }
  };

  return (
    <ContentLayout
      title="Anasayfa Hizmetleri"
      backLink="/dashboard"
      actionButton={{ label: "Yeni Hizmet Ekle", href: "/content/home/services/new" }}
    >
      <div className="mb-4">
        <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Dil seçin" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tr">{languageFlags.tr} Türkçe</SelectItem>
            <SelectItem value="en">{languageFlags.en} English</SelectItem>
            <SelectItem value="ar">{languageFlags.ar} العربية</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">Sıra</TableHead>
            <TableHead>Başlık</TableHead>
            <TableHead>Açıklama</TableHead>
            <TableHead>İkon</TableHead>
            <TableHead className="w-[150px]">İşlemler</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={5}>Hizmetler yükleniyor...</TableCell></TableRow>
          ) : services.length === 0 ? (
            <TableRow><TableCell colSpan={5}>Hiç hizmet yok.</TableCell></TableRow>
          ) : (
            services.map((service) => {
              const Icon = lucideIcons[service.icon] || null;
              return (
                <TableRow key={service.id}>
                  <TableCell>{service.order_num}</TableCell>
                  <TableCell className="font-medium">{service.title?.[selectedLanguage] || ''}</TableCell>
                  <TableCell className="max-w-[400px] truncate">{service.description?.[selectedLanguage] || ''}</TableCell>
                  <TableCell>{Icon ? <Icon className="h-5 w-5" /> : service.icon}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => moveService(service.id, "up")}> <MoveUp className="h-4 w-4" /> </Button>
                      <Button variant="ghost" size="icon" onClick={() => moveService(service.id, "down")}> <MoveDown className="h-4 w-4" /> </Button>
                      <Button variant="ghost" size="icon" asChild>
                        <a href={`/content/home/services/${service.id}/edit`}><Pencil className="h-4 w-4" /></a>
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteService(service.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </ContentLayout>
  );
}
